'use client'

import { useEffect, useRef, useState, ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { WagamourV2Modal } from '@/components/v2/WagamourV2Modal'
import { homeV2Text, homeV2Type } from '@/lib/home/homeV2Theme'

/** Temporary PWA audit logs — set localStorage DEBUG_PWA_LOCATION=1, remove when done. */
function debugPwaLocationModal(): boolean {
  return typeof window !== 'undefined' && localStorage.getItem('DEBUG_PWA_LOCATION') === '1'
}

interface ModalDialogProps {
  isOpen: boolean
  onClose: () => void
  children: ReactNode
  title?: string
  className?: string
  showCloseButton?: boolean
  preventClose?: boolean
  /** V2 glass skin — same handlers and children, Home / Play visual language. */
  visualVariant?: 'legacy' | 'v2'
}

/** Max height for modal panel: viewport minus safe areas and outer padding (mobile / notches). */
const MODAL_MAX_HEIGHT_STYLE = {
  maxHeight:
    'calc(100dvh - max(12px, env(safe-area-inset-top, 0px)) - max(12px, env(safe-area-inset-bottom, 0px)) - 16px)',
} as const

/**
 * Modal dialog: backdrop, focus trap, body scroll lock, Esc, high z-index above bottom nav.
 *
 * PWA / iOS standalone: content is portaled to document.body. WebKit often breaks
 * position:fixed + stacking under scrollable overflow ancestors; portaling avoids clipped modals.
 */
export function ModalDialog({
  isOpen,
  onClose,
  children,
  title,
  className,
  showCloseButton = true,
  preventClose = false,
  visualVariant = 'v2',
}: ModalDialogProps) {
  const modalRef = useRef<HTMLDivElement>(null)
  const previousActiveElement = useRef<HTMLElement | null>(null)
  const [portalEl, setPortalEl] = useState<HTMLElement | null>(null)

  useEffect(() => {
    setPortalEl(document.body)
  }, [])

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      previousActiveElement.current = document.activeElement as HTMLElement

      const originalOverflow = document.body.style.overflow
      const originalPaddingRight = document.body.style.paddingRight
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth

      document.body.style.overflow = 'hidden'
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`
      }

      const focusableElements = modalRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      const firstElement = focusableElements?.[0] as HTMLElement
      firstElement?.focus()

      return () => {
        document.body.style.overflow = originalOverflow
        document.body.style.paddingRight = originalPaddingRight
        previousActiveElement.current?.focus()
      }
    }
  }, [isOpen])

  // Handle Esc key
  useEffect(() => {
    if (!isOpen || preventClose) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose, preventClose])

  // Focus trap: keep focus within modal
  useEffect(() => {
    if (!isOpen) return

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return

      const focusableElements = modalRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      if (!focusableElements || focusableElements.length === 0) return

      const firstElement = focusableElements[0] as HTMLElement
      const lastElement = focusableElements[
        focusableElements.length - 1
      ] as HTMLElement

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault()
          lastElement.focus()
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault()
          firstElement.focus()
        }
      }
    }

    document.addEventListener('keydown', handleTab)
    return () => document.removeEventListener('keydown', handleTab)
  }, [isOpen])

  useEffect(() => {
    if (!isOpen || !portalEl) return
    if (!debugPwaLocationModal()) return
    queueMicrotask(() => {
      console.log('[PWA:ModalDialog] open, portaled to document.body', {
        portalTarget: portalEl === document.body ? 'body' : 'other',
      })
    })
  }, [isOpen, portalEl])

  if (!isOpen || !portalEl) return null

  if (visualVariant === 'v2') {
    return (
      <WagamourV2Modal
        isOpen={isOpen}
        onClose={onClose}
        preventClose={preventClose}
        showCloseButton={showCloseButton}
        className={className}
        header={
          title ? (
            <h2
              id="modal-title"
              className="text-lg font-bold leading-snug"
              style={homeV2Type(homeV2Text.headline)}
            >
              {title}
            </h2>
          ) : undefined
        }
      >
        {children}
      </WagamourV2Modal>
    )
  }

  return createPortal(
    <>
      {/* Backdrop — full viewport; clicks handled on scroll layer below for mobile dismiss */}
      <div
        className="fixed inset-0 z-[100000] bg-black/50 backdrop-blur-sm pointer-events-none"
        aria-hidden="true"
      />

      <div
        className="fixed inset-0 z-[100001] overflow-y-auto overscroll-y-contain"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        <div
          className="min-h-[100dvh] w-full flex items-center justify-center px-3 sm:px-4 box-border"
          style={{
            paddingTop: 'max(12px, env(safe-area-inset-top, 0px))',
            paddingBottom: 'max(12px, env(safe-area-inset-bottom, 0px))',
          }}
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget && !preventClose) {
              onClose()
            }
          }}
        >
          <div
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={title ? 'modal-title' : undefined}
            className={cn(
              'relative w-full max-w-md rounded-3xl bg-white shadow-2xl flex flex-col overflow-hidden mx-auto',
              className
            )}
            style={{
              ...MODAL_MAX_HEIGHT_STYLE,
              touchAction: 'manipulation',
              isolation: 'isolate',
            }}
            onClick={(e) => {
              e.stopPropagation()
            }}
            onMouseDown={(e) => {
              e.stopPropagation()
            }}
            onTouchStart={(e) => {
              e.stopPropagation()
            }}
          >
            {showCloseButton && (
              <div className="flex-shrink-0 flex justify-end items-center pt-2 pr-2 pb-1 bg-white rounded-t-3xl border-b border-gray-100/80">
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    if (!preventClose) {
                      onClose()
                    }
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                  }}
                  disabled={preventClose}
                  className={cn(
                    'p-2 rounded-full transition-colors',
                    preventClose
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:bg-gray-100'
                  )}
                  aria-label="Close modal"
                  aria-disabled={preventClose}
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
            )}

            <div
              className={cn(
                'overflow-y-auto overscroll-y-contain flex-1 min-h-0 touch-pan-y',
                showCloseButton ? 'px-6 sm:px-8 pb-6 sm:pb-8 pt-1' : 'p-6 sm:p-8'
              )}
            >
              {title && (
                <h2
                  id="modal-title"
                  className="text-2xl font-bold text-gray-800 mb-4"
                >
                  {title}
                </h2>
              )}

              {children}
            </div>
          </div>
        </div>
      </div>
    </>,
    portalEl
  )
}
